import { Timestamp } from '@framework/Timestamp';
import type { ViewModelAssetJSON, GroupId, UserId, ViewModelAssetId, ViewModelId } from 'balus-common';
import { ViewModelAssetURL } from '@user/pages/urls';
import { RecordRepository, RefBuilder, ServerValue } from '@framework/repository';
import { RTDBPath, StoragePath } from 'balus-common';
import { StorageRefBuilder } from '@framework/firebase/storage';

const MaxFileSize = 10 * 1024 * 1024;
const MaxAssetCount = 100;

export class ViewModelAsset {
  constructor(
    public readonly id: ViewModelAssetId,
    public readonly filename: string,
    public readonly fileSize: number,
    public readonly contentType: string,
    public readonly createdUserId: UserId,
    public readonly createdAt: Timestamp
  ) {}

  static load(dump: ViewModelAssetJSON): ViewModelAsset {
    const { id, fileSize, filename, contentType, createdAt, createdUserId } = dump;
    return new this(id, filename, fileSize, contentType, createdUserId, new Timestamp(createdAt));
  }

  dump(): ViewModelAssetJSON {
    const { id, fileSize, filename, contentType, createdAt, createdUserId } = this;
    return {
      id,
      filename,
      fileSize,
      contentType,
      createdUserId,
      createdAt: createdAt.toUnixTimestamp(),
    };
  }

  /**
   * 引数に指定された blob オブジェクトを Cloud Storage にアップロードして、
   * この asset オブジェクトを Realtime Database に保存します。
   * アップロードできない場合には false を返します。
   * @param groupId
   * @param viewModelId
   * @param blob
   * @returns
   */
  async upload(groupId: GroupId, viewModelId: ViewModelId, blob: Blob): Promise<boolean> {
    if (!this.contentType.startsWith('image/') || this.fileSize >= MaxFileSize) return false;

    const assets = await new RecordRepository(ViewModelAsset, RTDBPath.ViewModel.Asset.assetsPath(viewModelId)).get();

    if (Object.keys(assets).length >= MaxAssetCount) return false;

    const databaseRef = RefBuilder.ref(RTDBPath.ViewModel.Asset.assetPath(viewModelId, this.id));
    const storageRef = StorageRefBuilder.ref(StoragePath.viewModelAssetPath(groupId, viewModelId, this.id));

    // Cloud Storage にアップロードして、RTDBにもメタ情報を保存する
    await storageRef.put(blob, {
      contentType: this.contentType,
      contentDisposition: `inline; filename*=utf-8''${encodeURIComponent(this.filename)}`,
      customMetadata: {
        createdUserId: this.createdUserId,
        originalFilename: this.filename,
      },
    });
    await databaseRef.set({
      ...this.dump(),
      createdAt: ServerValue.TIMESTAMP,
    });

    return true;
  }

  /**
   * ビューモデル添付画像のURLを組み立てて返します。
   * @param groupId グループID
   * @param viewModelId ビューモデルID
   * @param baseUrl URL組み立て時のベースとなるURL
   */
  url(groupId: string, viewModelId: string, baseUrl: string): string {
    return new ViewModelAssetURL(groupId, viewModelId, this.id).url(baseUrl);
  }

  withCreatedUserId(createdUserId: UserId): ViewModelAsset {
    const { id, filename, fileSize, contentType } = this;
    return new ViewModelAsset(id, filename, fileSize, contentType, createdUserId, Timestamp.now());
  }
}
